import {
  Box,
  Center,
  Group,
  PasswordInput as MantinePasswordInput,
  type PasswordInputProps as MantinePasswordInputProps,
  Popover,
  PopoverProps,
  Progress,
  Text
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCheck, IconX } from '@tabler/icons-react'
import { fill, map, reduce } from 'lodash'
import { forwardRef, useImperativeHandle } from 'react'

export interface PasswordRequirement {
  pattern: RegExp
  label: string
}
export type PasswordRequirements = PasswordRequirement[]

const defaultRequirments: PasswordRequirements = [
  { pattern: /.{6}/, label: 'At least six characters' },
  { pattern: /\d/, label: 'Includes number' },
  { pattern: /[a-z]/, label: 'Includes lowercase letter' },
  { pattern: /[A-Z]/, label: 'Includes uppercase letter' },
  { pattern: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol' }
]

export interface PasswordInputHandle {
  closePopover(): void
  openPopover(): void
}
export interface PasswordInputProps extends MantinePasswordInputProps {
  popoverProps?: Omit<PopoverProps, 'children'>
  requirements?: PasswordRequirements | null
  value: string
}

export const PasswordInput = forwardRef<PasswordInputHandle, PasswordInputProps>(
  ({ requirements = defaultRequirments, value, popoverProps, ...props }, ref) => {
    const [popoverOpened, popoverHandler] = useDisclosure(false)
    const input = (
      <MantinePasswordInput
        mb='xs'
        value={value}
        {...props}
      />
    )

    const progressSections = requirements && fill(new Array(requirements.length), 0)
    const strength =
      requirements && Array.isArray(requirements) && requirements.length > 0
        ? Math.max(
            100 -
              (100 / (requirements.length + 1)) *
                reduce(requirements, (acc, requirement) => (!requirement.pattern.test(value) ? acc + 1 : acc), 0),
            10
          )
        : null

    useImperativeHandle(ref, () => ({
      closePopover: popoverHandler.close,
      openPopover: popoverHandler.open
    }))

    return requirements ? (
      <Popover
        opened={popoverOpened}
        {...popoverProps}
        position='bottom'
        width='target'
      >
        <Popover.Target>
          <div
            onFocusCapture={popoverHandler.open}
            onBlurCapture={popoverHandler.close}
          >
            {input}
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <Group
            spacing={5}
            grow
            mb='xs'
          >
            {map(progressSections, (_, idx) => (
              <Progress
                styles={{ bar: { transitionDuration: '0ms' } }}
                value={
                  value.length > 0 && idx === 0
                    ? 100
                    : strength && strength >= ((idx + 1) / requirements.length) * 100
                    ? 100
                    : 0
                }
                color={strength && strength > 80 ? 'teal' : strength && strength > 50 ? 'yellow' : 'red'}
                key={`strengthBar-${idx}`}
                size={4}
              />
            ))}
          </Group>
          {map(requirements, (requirement, idx) => {
            const meets = requirement.pattern.test(value)

            return (
              <Text
                color={meets ? 'teal' : 'red'}
                key={`requirement-${idx}`}
                mt='xs'
                size='sm'
              >
                <Center inline>
                  {meets ? (
                    <IconCheck
                      size={14}
                      stroke={1.5}
                    />
                  ) : (
                    <IconX
                      size={14}
                      stroke={1.5}
                    />
                  )}
                  <Box ml={7}>{requirement.label}</Box>
                </Center>
              </Text>
            )
          })}
        </Popover.Dropdown>
      </Popover>
    ) : (
      input
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
